import { Component, ChangeDetectionStrategy, signal, computed, inject, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CalculationStorageService, SaveCalculationRequest, CalculationData } from '../../services/calculation-storage.service';

@Component({
  selector: 'app-save-calculation-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    @if (isOpen()) {
      <div class="dialog-overlay" (click)="close()">
        <div class="dialog-content" (click)="$event.stopPropagation()">
          <div class="dialog-header">
            <h2>{{ currentCalculationId() ? 'Berechnung aktualisieren' : 'Berechnung speichern' }}</h2>
            <button class="close-btn" (click)="close()" aria-label="Schließen">&times;</button>
          </div>
          
          <div class="dialog-body">
            <div class="form-group">
              <label for="calc-name">Name der Berechnung *</label>
              <input 
                id="calc-name"
                type="text" 
                [value]="nameInput()" 
                (input)="updateName($event)"
                (blur)="validateName()"
                placeholder="z.B. Berlin - Hauptstraße 42"
                [class.error]="nameError()"
              />
              @if (nameError()) {
                <span class="error-message">{{ nameError() }}</span>
              }
            </div>
            
            @if (!currentCalculationId()) {
              <p class="hint">
                <strong>Tipp:</strong> Der Name wird automatisch aus Ort und Straße vorgeschlagen, Sie können ihn aber frei ändern.
              </p>
            }
          </div>
          
          <div class="dialog-footer">
            <button class="btn btn-secondary" (click)="close()">Abbrechen</button>
            <button 
              class="btn btn-primary" 
              (click)="save()"
              [disabled]="!isValid() || isSaving()"
            >
              {{ isSaving() ? 'Speichere...' : (currentCalculationId() ? 'Aktualisieren' : 'Speichern') }}
            </button>
          </div>
        </div>
      </div>
    }
  `,
  styles: [`
    .dialog-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    }
    
    .dialog-content {
      background: white;
      border-radius: 8px;
      max-width: 500px;
      width: 90%;
      max-height: 90vh;
      overflow-y: auto;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    }
    
    .dialog-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1.5rem;
      border-bottom: 1px solid #e5e7eb;
    }
    
    .dialog-header h2 {
      margin: 0;
      font-size: 1.5rem;
      color: #1f2937;
    }
    
    .close-btn {
      background: none;
      border: none;
      font-size: 2rem;
      color: #6b7280;
      cursor: pointer;
      padding: 0;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      transition: background-color 0.2s;
    }
    
    .close-btn:hover {
      background-color: #f3f4f6;
      color: #1f2937;
    }
    
    .dialog-body {
      padding: 1.5rem;
    }
    
    .form-group {
      margin-bottom: 1rem;
    }
    
    .form-group label {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: 600;
      color: #374151;
    }
    
    .form-group input {
      width: 100%;
      padding: 0.75rem;
      border: 2px solid #d1d5db;
      border-radius: 6px;
      font-size: 1rem;
      transition: border-color 0.2s;
    }
    
    .form-group input:focus {
      outline: none;
      border-color: var(--bright-blue, #3b82f6);
    }
    
    .form-group input.error {
      border-color: #ef4444;
    }
    
    .error-message {
      display: block;
      color: #ef4444;
      font-size: 0.875rem;
      margin-top: 0.25rem;
    }
    
    .hint {
      background: #eff6ff;
      border-left: 3px solid #3b82f6;
      padding: 0.75rem;
      border-radius: 4px;
      font-size: 0.875rem;
      color: #1e40af;
      margin-top: 1rem;
    }
    
    .dialog-footer {
      display: flex;
      gap: 0.75rem;
      justify-content: flex-end;
      padding: 1.5rem;
      border-top: 1px solid #e5e7eb;
    }
    
    .btn {
      padding: 0.75rem 1.5rem;
      border-radius: 6px;
      font-size: 1rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
      border: none;
    }
    
    .btn-secondary {
      background: #f3f4f6;
      color: #374151;
    }
    
    .btn-secondary:hover {
      background: #e5e7eb;
    }
    
    .btn-primary {
      background: var(--bright-blue, #3b82f6);
      color: white;
    }
    
    .btn-primary:hover:not(:disabled) {
      background: #2563eb;
    }
    
    .btn-primary:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SaveCalculationDialogComponent {
  private storageService = inject(CalculationStorageService);
  
  isOpen = signal(false);
  nameInput = signal('');
  nameError = signal('');
  isSaving = signal(false);
  
  // Aktuelle Berechnung (falls Update)
  currentCalculationId = signal<number | null>(null);
  
  // Daten die gespeichert werden sollen (von außen gesetzt)
  calculationData = signal<CalculationData>({});
  addressData = signal<{ country?: string, city?: string, address?: string, postalCode?: string }>({});
  
  // Callback wenn erfolgreich gespeichert
  onSaved = signal<((id: number, name: string) => void) | null>(null);
  
  // Callback für Toast-Nachrichten
  onToast = signal<((message: string, type: 'success' | 'error') => void) | null>(null);
  
  isValid = computed(() => {
    return this.nameInput().trim().length > 0 && !this.nameError();
  });
  
  /**
   * Dialog öffnen für neue Berechnung
   */
  openForNew(data: CalculationData, address: { country?: string, city?: string, address?: string, postalCode?: string }) {
    this.currentCalculationId.set(null);
    this.calculationData.set(data);
    this.addressData.set(address);
    
    // Generiere Namensvorschlag
    const suggestion = this.storageService.generateNameSuggestion(address.city, address.address);
    this.nameInput.set(suggestion);
    this.nameError.set('');
    this.isOpen.set(true);
  }
  
  /**
   * Dialog öffnen zum Aktualisieren
   */
  openForUpdate(id: number, currentName: string, data: CalculationData, address: { country?: string, city?: string, address?: string, postalCode?: string }) {
    this.currentCalculationId.set(id);
    this.calculationData.set(data);
    this.addressData.set(address);
    this.nameInput.set(currentName);
    this.nameError.set('');
    this.isOpen.set(true);
  }
  
  updateName(event: Event) {
    const input = event.target as HTMLInputElement;
    this.nameInput.set(input.value);
    this.nameError.set('');
  }
  
  async validateName() {
    const name = this.nameInput().trim();
    if (!name) {
      this.nameError.set('Name ist erforderlich');
      return;
    }
    
    try {
      const result = await this.storageService.checkNameExists(name, this.currentCalculationId() ?? undefined).toPromise();
      if (result?.exists) {
        this.nameError.set('Dieser Name existiert bereits. Bitte wählen Sie einen anderen Namen.');
      }
    } catch (error) {
      console.error('Fehler bei Namensvalidierung:', error);
    }
  }
  
  async save() {
    if (!this.isValid() || this.isSaving()) return;
    
    await this.validateName();
    if (this.nameError()) return;
    
    this.isSaving.set(true);
    
    try {
      const request: SaveCalculationRequest = {
        name: this.nameInput().trim(),
        country: this.addressData().country,
        city: this.addressData().city,
        address: this.addressData().address,
        postalCode: this.addressData().postalCode,
        calculationData: this.calculationData()
      };
      
      console.log('Sending calculation save request:', {
        name: request.name,
        hasData: !!request.calculationData,
        dataKeys: Object.keys(request.calculationData || {}).length
      });
      
      let result;
      if (this.currentCalculationId()) {
        result = await this.storageService.updateCalculation(this.currentCalculationId()!, request).toPromise();
      } else {
        result = await this.storageService.saveCalculation(request).toPromise();
      }
      
      console.log('Calculation saved successfully:', result);
      
      if (result) {
        const callback = this.onSaved();
        if (callback) {
          callback(result.calculationId, this.nameInput().trim());
        }
        this.close();
      }
    } catch (error: any) {
      console.error('Fehler beim Speichern - Full error:', error);
      console.error('Error status:', error?.status);
      console.error('Error message:', error?.message);
      console.error('Error error:', error?.error);
      
      // Prüfe ob es ein Validierungsfehler ist
      if (error?.error?.ValidationError) {
        const message = error.error.Message || 'Die Berechnung konnte nicht gespeichert werden. Einige Werte sind zu extrem.';
        const toastCallback = this.onToast();
        if (toastCallback) {
          toastCallback(message, 'error');
        }
        this.close();
        return;
      }
      
      if (error?.error?.message || error?.error?.Message) {
        this.nameError.set(error.error.message || error.error.Message);
      } else if (error?.message) {
        this.nameError.set(error.message);
      } else if (error?.status === 0) {
        this.nameError.set('Keine Verbindung zum Server. Bitte prüfen Sie ob das Backend läuft.');
      } else {
        this.nameError.set('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
      }
    } finally {
      this.isSaving.set(false);
    }
  }
  
  close() {
    this.isOpen.set(false);
    this.nameInput.set('');
    this.nameError.set('');
    this.isSaving.set(false);
    this.currentCalculationId.set(null);
  }
}
